// Reach Tool formatters: dates, durations, ids and schedule labels.
// Pure functions only — anything that needs state or the network lives elsewhere.



export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];


export function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}


export function formatDate(date, time) {
  return `${MONTHS[date.getMonth()]} ${ordinal(date.getDate())} ${date.getFullYear()} ${time}:00`;
}


// What the Next Run column says, from the scheduler's own state — never a guess.
// The four cases are genuinely different answers to "when does this run next":
//   Paused        — it doesn't; the stream is disabled.
//   Not scheduled — it can't; the scheduler recorded why (shown on hover via title).
//   Scheduling…   — the scheduler hasn't seen this stream yet (a fresh create or edit;
//                   the sweep fills it within minutes).
//   a timestamp   — the actual next occurrence, computed in the stream's own timezone.
export function nextRunLabel(stream) {
  if (!stream.enabled) return 'Paused';
  if (stream.scheduleError) return 'Not scheduled';
  if (!stream.nextRunAt) return 'Scheduling…';
  return formatRunTimestamp(stream.nextRunAt);
}


export const dateToKey = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export const keyToDate = key => key ? new Date(`${key}T12:00:00`) : null;

export const friendlyRangeDate = key => key ? keyToDate(key).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : 'Select date';


// First six characters of a stream's UUID — enough to tell rows apart by eye, short enough to
// stay on one line. Not an identifier in its own right: anything acting on a stream uses the
// full id, which the cell keeps in `title`.
export function shortStreamId(id) {
  return String(id || '').replace(/-/g, '').slice(0, 6);
}


// Postgres timestamps arrive as ISO strings. Rendered as date + minute in the viewer's locale:
// the second a run finished is never the question, but the day and hour are.
export function formatRunTimestamp(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return `${parsed.toISOString().slice(0, 10)} ${parsed.toTimeString().slice(0, 5)}`;
}


// Claim to finish — the time the work actually took, not counting how long it sat in the queue.
// Empty for a run that has not finished, rather than a number that keeps growing as you look
// at it.
export function formatDuration(claimedAt, finishedAt) {
  if (!claimedAt || !finishedAt) return '';
  const seconds = (new Date(finishedAt) - new Date(claimedAt)) / 1000;
  if (!Number.isFinite(seconds) || seconds < 0) return '';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
}


// Dates arrive as ISO strings from Postgres. Rendered short and locale-independent, because a
// token expiry read as month/day in one place and day/month in another is worse than useless.
export function formatConnectionDate(value) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
}

